import { Card, Dialog, DialogContent, DialogTitle, Divider, Grid, makeStyles } from "@material-ui/core";
import React, { useState } from "react";
import {
  BooleanInput,
  Button,
  Datagrid,
  FormDataConsumer,
  ListContextProvider,
  required,
  SelectInput,
  TextField,
  TextInput,
  useDataProvider,
  useNotify,
} from "react-admin";
import { Form } from "react-final-form";
import { CustomDatetime } from "../../../Components/CustomDatetime";
import { SectionTitle } from "../../../Components/SectionTitle";
import { Separator } from "../../../Components/Separator";
import ReliabilityReportRemarkTimeline from "../ReliabilityReportRemarks";
import { Add, Update, GetApp } from "@material-ui/icons";
import { ReliabilityReportRemarkCreateModel } from "../Models/ReliabilityReportRemarkCreateModel";
import { ExcelExport } from "../../../Helper/ExportExcel";
import { useAppDataProvider } from "../../../../src/dataprovider";
import { ReliabilityReportUpdateModel } from "../Models/ReliabilityReportModels";
import { ReliabilityReportStatusChoices } from "../../../../src/Enums/ReliabilityReportStatus";
import { ReliabilityReportTypeChoices } from "../../../../src/Enums/ReliabilityReportType";
import { useAuth } from "@airborne/react-admin";
import { ReliabilityRogueChoices, ReliabilityRogueStatus } from "../../../../src/Enums/ReliabilityRogueStatus";
import {
  ReliabilityRogueConfirmedReasonType,
  ReliabilityRogueReasonType,
  ReliabilityRogueRejectReasonType,
} from "../../../../src/Enums/ReliabilityRogueReasonType";
import { ReliabilityRogueUpdateModel } from "../Models/ReliabilityRogueModel";
import keyBy from "lodash/keyBy";
import { CustomDateField } from "../../../Components/CustomDateField";
import ReliabilityOtherConditions from "../ReliabilityOtherConditions";

const useStyles = makeStyles({
  redLabel: {
    "& .MuiFormControlLabel-label": {
      color: "red",
    },
    "& .MuiSwitch-track": {
      backgroundColor: "red !important",
      opacity: 0.5,
    },
    "& .MuiSwitch-thumb": {
      color: "red",
    },
    "& .MuiSwitch-colorSecondary.Mui-disabled + .MuiSwitch-track": {
      backgroundColor: "red !important",
    },
    "& .MuiSwitch-colorSecondary.Mui-disabled .MuiSwitch-thumb": {
      color: "red",
    },
  },
});

export const RogueDetail = props => {
  const classes = useStyles();
  const provider = useDataProvider();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({} as any);
  const notify = useNotify();
  const [isReliabilityReportButtonShow, setIsReliabilityReportButtonShow] = useState(false);
  const [isRogueButtonShow, setIsRogueButtonShow] = useState(false);
  const [isDisabledReportFields, setIsDisabledReportFields] = useState(true);
  const [isDisabledRogueFields, setIsDisabledRogueFields] = useState(true);
  const [isReasonRequired, setIsReasonRequired] = useState(false);
  const [isRogueConfirmed, setIsRogueConfirmed] = useState(false);

  const dataProvider = useAppDataProvider();
  const { user } = useAuth();

  React.useEffect(() => {
    getData();
  }, []);

  const getData = () => {
    provider
      .getOne("reliabilityrogues", {
        id: props.id,
      })
      .then(response => {
        setData(response.data);

        if (
          response.data.rogueStatus === ReliabilityRogueStatus.Rejected ||
          response.data.rogueStatus === ReliabilityRogueStatus.Confirmed
        ) {
          setIsReasonRequired(true);
          if (response.data.rogueStatus === ReliabilityRogueStatus.Confirmed) {
            setIsRogueConfirmed(true);
          } else {
            setIsRogueConfirmed(false);
          }
        } else {
          setIsReasonRequired(false);
        }
      })
      .catch(error => {});
  };

  const editReliabilityReport = e => {
    setIsReliabilityReportButtonShow(true);
    setIsDisabledReportFields(false);
  };

  const editReliabilityReportButtonClick = formValues => e => {
    let reliabilityReport: ReliabilityReportUpdateModel = new ReliabilityReportUpdateModel();
    reliabilityReport.Id = data?.report?.id;
    reliabilityReport.Pn = data?.report?.pn;
    reliabilityReport.Sn = data?.report?.sn;
    reliabilityReport.GoodsRcvdBatch = data?.report?.goodsRcvdBatch;
    reliabilityReport.Chapter = data?.report?.chapter;
    reliabilityReport.ReportStatus = formValues.reportStatus;
    reliabilityReport.ReportType = formValues.reportType;
    reliabilityReport.TargetDate = formValues.targetDate;

    dataProvider
      .putReliabilityReport(reliabilityReport)
      .then(result => {
        notify(`Reliability Report Updated`, "success", false, true);
        setIsReliabilityReportButtonShow(false);
        setIsDisabledReportFields(true);
      })
      .catch(result => notify(`${result}`, "error", false, true))
      .finally(() => setLoading(false));
  };

  const CancelEditingReliabilityReport = e => {
    setIsReliabilityReportButtonShow(false);
    setIsDisabledReportFields(true);
  };

  const editRogue = e => {
    setIsRogueButtonShow(true);
    setIsDisabledRogueFields(false);
  };

  const editRogueButtonClick = e => {
    let reliabilityRogue: ReliabilityRogueUpdateModel = new ReliabilityRogueUpdateModel();
    reliabilityRogue.Id = data?.id;
    reliabilityRogue.ReportId = data?.report?.id;
    reliabilityRogue.GoodsRcvdBatch = data?.goodsRcvdBatch;
    reliabilityRogue.AfterRemovalOrderType = data?.afterRemovalOrderType;
    reliabilityRogue.AfterRemovalOrderNumber = data?.afterRemovalOrderNumber;
    reliabilityRogue.AfterRemovalOrderStatus = data?.afterRemovalOrderStatus;
    reliabilityRogue.AfterRemovalVendorType = data?.afterRemovalVendorType;
    reliabilityRogue.AfterRemovalVendor = data?.afterRemovalVendor;
    reliabilityRogue.AfterRemovalCondition = data?.afterRemovalCondition;
    reliabilityRogue.AfterRemovalDate = data?.afterRemovalDate;
    reliabilityRogue.IsThreeConsecutive = data?.isThreeConsecutive;
    reliabilityRogue.IsFiveConsecutiveAvg = data?.isFiveConsecutiveAvg;
    reliabilityRogue.AverageFlightHourForFiveConsecutive = data?.averageFlightHourForFiveConsecutive;
    reliabilityRogue.RogueStatus = e.rogueStatus;
    reliabilityRogue.Reason = e.reason;
    reliabilityRogue.OtherReason = e.otherReason;

    dataProvider
      .putReliabilityRogue(reliabilityRogue)
      .then(result => {
        notify(`Reliability Rogue  Updated`, "success", false, true);
        setIsRogueButtonShow(false);
        setIsDisabledRogueFields(true);
      })
      .catch(result => notify(`${result}`, "error", false, true))
      .finally(() => setLoading(false));
  };

  const CancelEditingRogue = e => {
    setIsRogueButtonShow(false);
    setIsDisabledRogueFields(true);
  };

  const AddReliabilityReportRemarkButton = props => {
    const [open, setOpen] = useState(false);

    const handleClick = e => {
      e.stopPropagation();
      setOpen(true);
    };
    const handleClose = () => {
      setOpen(false);
    };

    const AddReliabilityReportRemarkButtonClick = props => e => {
      let reliabilityRemark: ReliabilityReportRemarkCreateModel = new ReliabilityReportRemarkCreateModel();

      reliabilityRemark.ReliabilityReportId = props.reliabilityReportId;
      reliabilityRemark.IsVisibleToCustomer = e.isVisibleToCustomer;
      reliabilityRemark.Remark = e.remark;

      dataProvider
        .postReliabilityReportRemark(reliabilityRemark)
        .then(result => {
          notify(`Reliability Report Remark Added`, "success", false, true);
          history.go(0);
          setOpen(false);
        })
        .catch(result => notify(`${result}`, "error", false, true))
        .finally(() => setLoading(false));
    };

    const ClosePopup = () => {
      setOpen(false);
    };

    return (
      <>
        <Button label="Add Remark" disabled={loading} size="medium" variant="contained" onClick={handleClick}>
          {<Add />}
        </Button>

        <Dialog open={open} onClose={handleClose}>
          <DialogTitle>Reliability Report Remark</DialogTitle>
          <DialogContent>
            <Form
              onSubmit={AddReliabilityReportRemarkButtonClick(props)}
              render={({ handleSubmit }) => (
                <form>
                  <TextInput source="remark" label="remark" size="medium" fullWidth variant="outlined" />
                  <Grid item xs={12}>
                    <BooleanInput label="Is Visible To Customer" source="isVisibleToCustomer" defaultValue={false} />
                  </Grid>
                  <Button label={"Add Remark!"} size="large" variant="contained" onClick={handleSubmit}>
                    <Update />
                  </Button>
                  <Button label={"Cancel!"} size="large" variant="contained" onClick={ClosePopup} color="secondary">
                    <Update />
                  </Button>
                </form>
              )}
            />
          </DialogContent>
        </Dialog>
      </>
    );
  };

  const StatusChange = e => {
    e.target.value == ReliabilityRogueStatus.Confirmed ? setIsRogueConfirmed(true) : setIsRogueConfirmed(false);
    e.target.value == ReliabilityRogueStatus.Open ? setIsReasonRequired(false) : setIsReasonRequired(true);
  };

  const postRowStyle = record => ({
    backgroundColor: record.isInitialRogueCandidate ? "#ff2d00" : "",
  });

  const ExportFailCyclesToExcel = () => {
    if (!data?.reliabilityRogueFailCycle || data.reliabilityRogueFailCycle.length === 0) {
      notify("No data to export", "warning", false, false);
      return;
    }

    const exportData = data.reliabilityRogueFailCycle.map(record => ({
      "Batch Number": record.batch,
      "Ref No": record.refNo,
      "Install Date": record.installDate,
      "Remove Date": record.removeDate,
      Customer: record.customer,
      "Flight Hour": record.flightHour,
      "Before Removal Vendor Type": record.beforeRemovalVendorType,
      "Before Removal Vendor": record.beforeRemovalVendor,
      "Before Removal Condition": record.beforeRemovalCondition,
    }));

    const columns = Object.keys(exportData[0])
      .filter(key => key !== "isInitialRogueCandidate")
      .map(header => ({ header }));

    const styleConfig = {
      rowStyleCondition: (row, rowIndex) => exportData[rowIndex]?.isInitialRogueCandidate,
      rowStyle: {
        fill: {
          type: "pattern" as const,
          pattern: "solid" as const,
          fgColor: { argb: "FFff2d00" },
        },
      },
    };

    ExcelExport(columns, exportData, "Rogue Candidate Fail Cycles", user, styleConfig, false);
  };

  return (
    <>
      <Form
        onSubmit={() => {}}
        render={({ handleSubmit, form, values }) => {
          return (
            <form>
              <Card>
                <Grid container spacing={0} style={{ padding: 25 }}>
                  <Grid item xs={12} sm={12} md={12} lg={12}>
                    <SectionTitle label="Reliability Report Details" />
                    <Divider style={{ padding: 0 }} />
                    <Separator />
                  </Grid>

                  <Grid item xs={8} sm={8} md={4} lg={3} style={{ margin: 25 }}>
                    <TextInput
                      source="goodsRcvdBatch"
                      label="Goods Received Batch"
                      initialValue={data?.report?.goodsRcvdBatch}
                      fullWidth
                      disabled
                      variant="outlined"
                    />
                    <TextInput
                      source="chapter"
                      label="Chapter"
                      initialValue={data?.report?.chapter}
                      fullWidth
                      disabled
                      variant="outlined"
                    />
                    <SelectInput
                      source="reportStatus"
                      label="Report Status"
                      initialValue={data?.report?.reportStatus}
                      choices={ReliabilityReportStatusChoices}
                      optionText="key"
                      optionValue="value"
                      fullWidth
                      disabled={isDisabledReportFields}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={8} sm={8} md={4} lg={3} style={{ margin: 25 }}>
                    <TextInput
                      source="pn"
                      label="PN"
                      initialValue={data?.report?.pn}
                      fullWidth
                      disabled
                      variant="outlined"
                    />
                    <TextInput
                      source="pnDescription"
                      initialValue={data?.pnDescription}
                      label="PN Description"
                      fullWidth
                      disabled
                      variant="outlined"
                    />
                    <CustomDatetime
                      source="targetDate"
                      label="Target Date"
                      initialValue={data?.report?.targetDate}
                      fullWidth
                      showTime
                      disabled={isDisabledReportFields}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={8} sm={8} md={4} lg={3} style={{ margin: 25 }}>
                    <TextInput
                      source="sn"
                      label="SN"
                      initialValue={data?.report?.sn}
                      fullWidth
                      disabled
                      variant="outlined"
                    />
                    <TextInput
                      source="pnSubCategory"
                      label="SUB Category"
                      initialValue={data?.pnSubCategory}
                      fullWidth
                      disabled
                      variant="outlined"
                    />
                    <SelectInput
                      source="reportType"
                      label="Report Type"
                      initialValue={data?.report?.reportType}
                      choices={ReliabilityReportTypeChoices}
                      optionText="key"
                      optionValue="value"
                      fullWidth
                      disabled
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={0} style={{ padding: 25, justifyContent: "start", margin: 10 }}>
                  <SectionTitle label="Reliability Other Condition" />
                  {data?.report?.goodsRcvdBatch != undefined && (
                    <ReliabilityOtherConditions goodsRcvdBatch={data?.report?.goodsRcvdBatch} />
                  )}
                </Grid>
                <Grid container spacing={0} style={{ padding: 25, justifyContent: "end" }}>
                  {!user?.isCustomer && !isReliabilityReportButtonShow && (
                    <Button
                      label="Edit Reliability Report"
                      disabled={loading}
                      size="medium"
                      variant="contained"
                      onClick={editReliabilityReport}
                      style={{ margin: "8px", background: "#ffc107" }}
                    >
                      {<Update />}
                    </Button>
                  )}
                  {isReliabilityReportButtonShow && (
                    <Button
                      label="Edit"
                      disabled={loading}
                      size="medium"
                      variant="contained"
                      onClick={editReliabilityReportButtonClick(values)}
                      style={{ margin: "8px", background: "#4caf50" }}
                    >
                      {<Update />}
                    </Button>
                  )}
                  {isReliabilityReportButtonShow && (
                    <Button
                      label="Cancel"
                      disabled={loading}
                      size="medium"
                      variant="contained"
                      onClick={CancelEditingReliabilityReport}
                      style={{ margin: "8px", background: "#ffc107" }}
                    >
                      {<Update />}
                    </Button>
                  )}
                </Grid>

                <ReliabilityReportRemarkTimeline
                  reliabilityReportRemark={data?.reliabilityReportRemark}
                  refresh={getData}
                  isCustomerRelated={false}
                />
                <Grid container spacing={0} style={{ padding: 25, justifyContent: "end" }}>
                  {!user?.isCustomer && <AddReliabilityReportRemarkButton reliabilityReportId={data?.report?.id} />}
                </Grid>
              </Card>
            </form>
          );
        }}
      ></Form>

      <Separator />

      <Form
        initialValues={data}
        onSubmit={editRogueButtonClick}
        render={({ handleSubmit, form, values }) => {
          return (
            <form>
              <Card>
                <Grid container spacing={0} style={{ padding: 25 }}>
                  <Grid item xs={12} sm={12} md={12} lg={12}>
                    <SectionTitle label="Rogue Candidate Details" />
                    <Divider style={{ padding: 0 }} />
                    <Separator />
                  </Grid>
                  <Grid item xs={8} sm={8} md={4} lg={3} style={{ margin: 25 }}>
                    <TextInput
                      source="afterRemovalOrderNumber"
                      label="After Removal Order Number"
                      initialValue={data?.afterRemovalOrderNumber}
                      fullWidth
                      disabled
                      variant="outlined"
                    />
                    <CustomDatetime
                      source="afterRemovalDate"
                      label="After Removal Date"
                      initialValue={data?.afterRemovalDate}
                      fullWidth
                      showTime
                      disabled
                      variant="outlined"
                    />
                    <BooleanInput
                      source="isThreeConsecutive"
                      initialValue={data?.isThreeConsecutive}
                      label="3 Consecutive Failures Under 1000 FH"
                      disabled
                      fullWidth
                      variant="outlined"
                      className={data?.isThreeConsecutive ? classes.redLabel : undefined}
                    />
                    <BooleanInput
                      source="isFiveConsecutiveAvg"
                      initialValue={data?.isFiveConsecutiveAvg}
                      label="5 Consecutive Failures Average Under 1000 FH"
                      disabled
                      fullWidth
                      variant="outlined"
                      className={data?.isFiveConsecutiveAvg ? classes.redLabel : undefined}
                    />
                  </Grid>
                  <Grid item xs={8} sm={8} md={4} lg={3} style={{ margin: 25 }}>
                    <TextInput
                      source="afterRemovalCondition"
                      label="After Removal Condition"
                      initialValue={data?.afterRemovalCondition}
                      fullWidth
                      disabled
                      variant="outlined"
                    />
                    <TextInput
                      source="afterRemovalVendorType"
                      label="After Removal  Vendor Type"
                      initialValue={data?.afterRemovalVendorType}
                      fullWidth
                      disabled
                      variant="outlined"
                    />
                    <TextInput
                      source="afterRemovalVendor"
                      label="After Removal Vendor"
                      initialValue={data?.afterRemovalVendor}
                      fullWidth
                      disabled
                      variant="outlined"
                    />
                    <TextInput
                      source="averageFlightHourForFiveConsecutive"
                      label="Average FH For 5 Consecutive Failures"
                      initialValue={data?.afterRemovalOrderType}
                      fullWidth
                      disabled
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={8} sm={8} md={4} lg={3} style={{ margin: 25 }}>
                    <BooleanInput
                      source="isHardTime"
                      initialValue={data?.isHardTime}
                      label="Is Hard Time"
                      disabled
                      fullWidth
                      variant="outlined"
                      className={data?.isHardTime ? classes.redLabel : undefined}
                    />
                    <SelectInput
                      source="rogueStatus"
                      label="Rogue Candidate Status"
                      initialValue={data?.rogueStatus}
                      choices={ReliabilityRogueChoices}
                      optionText="key"
                      optionValue="value"
                      fullWidth
                      onChange={StatusChange}
                      disabled={isDisabledRogueFields}
                      variant="outlined"
                    />

                    {isReasonRequired && (
                      <SelectInput
                        source="reason"
                        label={isRogueConfirmed ? "Confirm Action" : "Reject Reason"}
                        initialValue={data?.reason}
                        choices={
                          isRogueConfirmed ? ReliabilityRogueConfirmedReasonType : ReliabilityRogueRejectReasonType
                        }
                        optionText="key"
                        optionValue="value"
                        fullWidth
                        disabled={isDisabledRogueFields}
                        variant="outlined"
                      />
                    )}

                    <FormDataConsumer>
                      {({ formData, ...rest }) =>
                        formData.reason === ReliabilityRogueReasonType.Other &&
                        !user?.isCustomer && (
                          <TextInput
                            source="otherReason"
                            label="Other Reason"
                            initialValue={data?.otherReason}
                            fullWidth
                            variant="outlined"
                            validate={required()}
                            disabled={isDisabledRogueFields}
                            multiline
                            {...rest}
                          />
                        )
                      }
                    </FormDataConsumer>
                  </Grid>
                </Grid>
                <Grid container spacing={0} style={{ padding: 25, justifyContent: "start", margin: 10 }}>
                  <SectionTitle label="Reliability Rogue Fail Cycles" />
                  <ListContextProvider
                    value={{
                      data: keyBy(data?.reliabilityRogueFailCycle, "id"),
                      ids: data?.reliabilityRogueFailCycle?.map?.(({ id }) => id),
                      currentSort: { field: "id", order: "ASC" },
                    }}
                  >
                    <Datagrid rowStyle={postRowStyle}>
                      <TextField label="Batch Number" source="batch" />
                      <TextField label="Ref NO" source="refNo" />
                      <CustomDateField source="installDate" label="Install Date " showTime />
                      <CustomDateField source="removeDate" label="Remove Date " showTime />
                      <TextField label="Customer" source="customer" />
                      <TextField label="Flight Hour" source="flightHour" />
                      <TextField label="Before Removal Vendor Type" source="beforeRemovalVendorType" />
                      <TextField label="Before Removal Vendor" source="beforeRemovalVendor" />
                      <TextField label="Before Removal Condition" source="beforeRemovalCondition" />
                    </Datagrid>
                  </ListContextProvider>
                </Grid>
                <Grid container spacing={0} style={{ padding: 25, justifyContent: "end" }}>
                  {!user?.isCustomer && !isRogueButtonShow && (
                    <Button
                      label="Edit Rogue Candidate"
                      disabled={loading}
                      size="medium"
                      variant="contained"
                      onClick={editRogue}
                      style={{ margin: "8px", background: "#ffc107" }}
                    >
                      {<Update />}
                    </Button>
                  )}
                  {isRogueButtonShow && (
                    <Button
                      label="Edit"
                      disabled={loading}
                      size="medium"
                      variant="contained"
                      onClick={handleSubmit}
                      style={{ margin: "8px", background: "#4caf50" }}
                    >
                      {<Update />}
                    </Button>
                  )}
                  {isRogueButtonShow && (
                    <Button
                      label="Cancel"
                      disabled={loading}
                      size="medium"
                      variant="contained"
                      onClick={CancelEditingRogue}
                      style={{ margin: "8px", background: "#ffc107" }}
                    >
                      {<Update />}
                    </Button>
                  )}
                  {data?.reliabilityRogueFailCycle?.length > 0 && (
                    <Button
                      label="Export"
                      size="medium"
                      variant="contained"
                      onClick={ExportFailCyclesToExcel}
                      disabled={loading}
                      style={{ margin: "8px" }}
                    >
                      <GetApp />
                    </Button>
                  )}
                </Grid>
              </Card>
            </form>
          );
        }}
      ></Form>
    </>
  );
};
export default RogueDetail;
